// A device identity for somebody who is never going to sign up.
//
// Nobody makes an account at a shotgun start. They have a cart, a phone in one hand and
// four minutes before the horn. So the scoring page signs in ANONYMOUSLY: Supabase mints a
// real `auth.users` row with no email and no password, the phone keeps the refresh token,
// and `auth.uid()` becomes the thing that owns a seat on a card.
//
// That uid is the whole security model for a player. `claim_entrant` writes it to
// `entrants.claimed_by`, and every RLS policy on the scoring tables resolves through it
// (`my_round_ids()` → the rounds of teams this device holds a seat on). Losing the session
// means losing the ability to write to the card, which is why persistSession matters and
// why this must never sign in twice and strand the first identity.
//
// IDEMPOTENCE IS THE POINT. React 19 StrictMode runs effects twice in development, and the
// page can ask for a session from several places at once (the route mount, a retry button,
// a resumed round). A module-level promise makes all of those the same single sign-in.

import { useCallback, useEffect, useState } from 'react';
import { errText, supabase } from './supabase';

/** The one in-flight (or settled) sign-in. Shared by every caller in the tab. */
let inFlight: Promise<string> | null = null;

/**
 * The device's user id, signing in anonymously the first time and reusing it after.
 *
 * Safe to call from anywhere, any number of times. On failure the cached promise is torn
 * down so the next caller genuinely retries rather than re-throwing a stale error — a
 * golfer under a tree line will fail this call and then succeed thirty seconds later.
 */
export async function ensureAnonSession(): Promise<string> {
  if (inFlight) return inFlight;

  const attempt = (async (): Promise<string> => {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;

    const existing = data.session?.user?.id;
    if (existing) return existing;

    const { data: signed, error: signInError } = await supabase.auth.signInAnonymously();
    if (signInError) throw signInError;

    const id = signed.session?.user?.id ?? signed.user?.id;
    if (!id) throw new Error('Signed in but got no device identity back');
    return id;
  })();

  inFlight = attempt;
  try {
    return await attempt;
  } catch (err) {
    if (inFlight === attempt) inFlight = null;
    throw err;
  }
}

export type AnonSession = {
  /** The device's `auth.uid()`, or null while it is still being established. */
  userId: string | null;
  /** True once there is a usable identity. */
  ready: boolean;
  /** Why there is no identity, in words worth showing somebody. */
  error: string | null;
  /** Try again after a failure. */
  retry: () => void;
};

/** `ensureAnonSession` as a hook, with the error and a retry the page can render. */
export function useAnonSession(): AnonSession {
  const [userId, setUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setError(null);

    ensureAnonSession().then(
      (id) => {
        if (alive) setUserId(id);
      },
      (err: unknown) => {
        if (alive) setError(errText(err));
      }
    );

    return () => {
      alive = false;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { userId, ready: userId !== null, error, retry };
}
